import './bootstrap';
import Alpine from 'alpinejs';
import './track';

window.Alpine = Alpine;

// Dynamically load Swiper when needed
window.loadSwiper = () => {
    if (window.Swiper) return Promise.resolve(window.Swiper);

    // Dynamically load CSS via Vite
    import('swiper/css/bundle').catch(e => console.warn('Swiper CSS load failed', e));

    return import('swiper/bundle').then(({ default: Swiper }) => {
        window.Swiper = Swiper;
        window.dispatchEvent(new CustomEvent('swiper-ready'));
        return Swiper;
    });
};

// Check for swiper elements and load if found
const checkSwiper = () => {
    if (document.querySelector('.swiper')) {
        window.loadSwiper();
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkSwiper);
} else {
    checkSwiper();
}
// Also check for dynamic content
const SwiperObserver = new MutationObserver((mutations) => {
    if (document.querySelector('.swiper')) {
        window.loadSwiper();
        SwiperObserver.disconnect();
    }
});
SwiperObserver.observe(document.body, { childList: true, subtree: true });


import React, { Suspense, lazy } from 'react';

import { createRoot } from 'react-dom/client';

// Lazy load components
const ListingGallery = lazy(() => import('./components/ListingGallery'));
const FavoriteButton = lazy(() => import('./components/FavoriteButton'));
const UserMenu = lazy(() => import('./components/UserMenu'));
const ListingForm = lazy(() => import('./components/ListingForm'));
const ListingRemovalManager = lazy(() => import('./components/ListingRemovalManager'));
const RecentlyViewedSection = lazy(() => import('./components/RecentlyViewedSection'));

// Helper for Suspense wrapping
const withSuspense = (Component, props = {}) => (
    <Suspense fallback={<div className="animate-pulse bg-gray-100 rounded-2xl h-10 w-full"></div>}>
        <Component {...props} />
    </Suspense>
);

// Mount React Gallery if container exists
const galleryRoot = document.getElementById('react-gallery-root');
if (galleryRoot) {
    const root = createRoot(galleryRoot);
    root.render(withSuspense(ListingGallery));
}

// Mount Favorite Buttons
const favoriteBtns = document.querySelectorAll('.react-favorite-button');
favoriteBtns.forEach(btn => {
    const root = createRoot(btn);
    const listingId = btn.getAttribute('data-listing-id');
    root.render(withSuspense(FavoriteButton, { listingId }));
});

// Mount User Menu
const userMenuContainer = document.getElementById('user-menu-container');
if (userMenuContainer) {
    const root = createRoot(userMenuContainer);
    const translations = JSON.parse(userMenuContainer.dataset.translations || '{}');
    const routes = JSON.parse(userMenuContainer.dataset.routes || '{}');
    root.render(withSuspense(UserMenu, { translations, routes }));
}

// Mount Listing Form
const listingFormRoot = document.getElementById('listing-form-root');
if (listingFormRoot) {
    try {
        const safeParse = (key, defaultVal = {}) => {
            try {
                const val = listingFormRoot.dataset[key];
                return val ? JSON.parse(val) : defaultVal;
            } catch (e) {
                console.error(`Error parsing ${key}:`, e);
                return defaultVal;
            }
        };

        const root = createRoot(listingFormRoot);
        const props = {
            initialData: safeParse('initialData', {}),
            brands: safeParse('brands', []),
            models: safeParse('models', {}),
            fuels: safeParse('fuels', []),
            transmissions: safeParse('transmissions', []),
            colors: safeParse('colors', []),
            cities: safeParse('cities', []),
            specsCategories: safeParse('specsCategories', []),
            routes: safeParse('routes', {}),
            csrfToken: listingFormRoot.dataset.csrfToken || '',
            recaptchaSiteKey: listingFormRoot.dataset.recaptchaSiteKey || '',
            errors: safeParse('errors', {}),
            userMobile: listingFormRoot.dataset.userMobile || null,
        };
        console.log("Mounting ListingForm with props:", props);
        root.render(withSuspense(ListingForm, props));
    } catch (err) {
        console.error("Failed to mount ListingForm:", err);
    }
}

// Mount Removal Managers
const mountRemovalManagers = (parent = document) => {
    const removalContainers = parent.querySelectorAll('.react-listing-removal');
    removalContainers.forEach(container => {
        if (container.dataset.reactMounted) return;
        try {
            const root = createRoot(container);
            const props = {
                listingId: container.dataset.listingId,
                reasons: JSON.parse(container.dataset.reasons || '[]'),
                translations: JSON.parse(container.dataset.translations || '{}'),
                variant: container.dataset.variant || 'button',
                buttonClass: container.dataset.buttonClass || '',
            };
            root.render(withSuspense(ListingRemovalManager, props));
            container.dataset.reactMounted = 'true';
        } catch (err) {
            console.error("Failed to mount ListingRemovalManager:", err);
        }
    });
};

// Listen for dynamic content updates
window.addEventListener('owner-actions-loaded', (event) => {
    if (event.detail && event.detail.element) {
        mountRemovalManagers(event.detail.element);
    }
});

// Initial mount
mountRemovalManagers();

// Mount Recently Viewed Section
const recentlyViewedRoot = document.getElementById('recently-viewed-root');
if (recentlyViewedRoot) {
    try {
        const root = createRoot(recentlyViewedRoot);
        const translations = JSON.parse(recentlyViewedRoot.dataset.translations || '{}');
        const locale = recentlyViewedRoot.dataset.locale || 'ar';
        root.render(withSuspense(RecentlyViewedSection, { translations, locale }));
    } catch (err) {
        console.error("Failed to mount RecentlyViewedSection:", err);
    }
}

Alpine.start();

window.addEventListener('scroll', function () {
    const header = document.getElementById('main-header');
    if (window.scrollY > 50) {
        // Desktop: Shrink from 128px to 100px
        if (header.classList.contains('md:h-[128px]')) {
            header.classList.replace('md:h-[128px]', 'md:h-[110px]');
            header.classList.add('shadow-md');
            header.classList.add('border-none');
        }
    } else {
        // Desktop: Expand back to 128px
        if (header.classList.contains('md:h-[110px]')) {
            header.classList.replace('md:h-[110px]', 'md:h-[128px]');
            header.classList.remove('shadow-md');
            header.classList.remove('border-none');
        }
    }
});


document.addEventListener('DOMContentLoaded', function () {
    /**
     * Reusable Sliding Indicator Logic
     * @param {Object} config
     * @param {string} config.containerId - ID of the container
     * @param {string} config.itemSelector - Selector for items (buttons/links)
     * @param {string} config.indicatorId - ID of the sliding dot/line
     * @param {string|null} config.activeClass - Class used for active state (default: 'active')
     * @param {Function|null} config.onClick - Callback when an item is clicked (receives item)
     */
    function setupSlidingIndicator({ containerId, itemSelector, indicatorId, activeClass = 'active', onClick = null }) {
        const container = document.getElementById(containerId);
        if (!container) return;

        const indicator = document.getElementById(indicatorId);
        const items = container.querySelectorAll(itemSelector);
        let activeItem = container.querySelector(`${itemSelector}.${activeClass}`) || container.querySelector(`${itemSelector}.active-link`); // Fallback for nav

        let slideTimeout;

        function moveIndicatorTo(element, isLine = false) {
            if (!indicator) return;
            if (!element) {
                indicator.style.opacity = '0';
                return;
            }

            // Batch layout reads in one frame, writes in the next — prevents forced reflow
            requestAnimationFrame(() => {
                const containerRect = container.getBoundingClientRect();
                const rect = element.getBoundingClientRect();
                const relativeLeft = rect.left - containerRect.left;
                const width = rect.width;

                // Batch all style writes in the next frame
                requestAnimationFrame(() => {
                    if (isLine) {
                        indicator.style.width = `${width}px`;
                        indicator.style.height = '2px';
                        indicator.style.left = `${relativeLeft}px`;
                        indicator.style.borderRadius = '4px';
                    } else {
                        const dotWidth = 6;
                        const centerOffset = (width - dotWidth) / 2;
                        indicator.style.width = `${dotWidth}px`;
                        indicator.style.height = '2px';
                        indicator.style.left = `${relativeLeft + centerOffset}px`;
                        indicator.style.borderRadius = '30px';
                        indicator.style.backgroundColor = '';
                    }
                    indicator.style.opacity = '1';
                });
            });
        }

        // Initial Position (Dot)
        if (activeItem) {
            setTimeout(() => moveIndicatorTo(activeItem, false), 50);
        }

        items.forEach(item => {
            // Hover Effects
            item.addEventListener('mouseenter', () => {
                clearTimeout(slideTimeout);
                // 1. Move immediately as DOT
                moveIndicatorTo(item, false);

                // 2. Expand to LINE after delay
                slideTimeout = setTimeout(() => {
                    moveIndicatorTo(item, true);
                }, 200);
            });

            // Click Handling
            item.addEventListener('click', (e) => {
                activeItem = item; // Update local active ref

                // Optional specific logic (e.g. tab switching)
                if (onClick) {
                    onClick(item);
                }

                // If the click logic updates classes elsewhere, we might need to verify active state,
                // but usually setting activeItem here is enough for the visual logic.

                // Ensure it stays as line or dot? Usually click means "focus",
                // but if mouse is still hovering, it should stay line.
                // If touch, it might be different. For now, matching hover behavior (Line if hovering).
                if (item.matches(':hover')) {
                    clearTimeout(slideTimeout);
                    moveIndicatorTo(item, true);
                } else {
                    moveIndicatorTo(item, false);
                }
            });
        });

        // Mouse Leave - Return to Active
        container.addEventListener('mouseleave', () => {
            clearTimeout(slideTimeout);
            if (activeItem) {
                moveIndicatorTo(activeItem, false); // Return as Dot
            } else if (indicator) {
                indicator.style.opacity = '0';
            }
        });

        // Resize Handling
        window.addEventListener('resize', () => {
            clearTimeout(slideTimeout);
            if (activeItem) moveIndicatorTo(activeItem, false);
        });
    }

    // 1. Setup GrandFilter Tabs
    setupSlidingIndicator({
        containerId: 'tabs-container',
        itemSelector: '.tab-btn',
        indicatorId: 'sliding-dot',
        activeClass: 'active',
        onClick: (clickedTab) => {
            // Existing logic to switch content and classes
            const contents = document.querySelectorAll('.tab-content');
            const allTabs = document.querySelectorAll('.tab-btn');
            const targetId = clickedTab.getAttribute('data-target');

            // Switch Content
            contents.forEach(content => {
                content.classList.add('hidden');
                content.classList.remove('opacity-100');
            });
            const targetContent = document.getElementById(targetId);
            if (targetContent) {
                targetContent.classList.remove('hidden');
                setTimeout(() => targetContent.classList.add('opacity-100'), 10);
            }

            // Switch Text Classes
            allTabs.forEach(t => {
                t.classList.remove('text-primary', 'font-bold', 'active');
                t.classList.add('text-nav-color', 'font-medium');
            });
            clickedTab.classList.remove('text-nav-color', 'font-medium');
            clickedTab.classList.add('text-primary', 'font-bold', 'active');
        }
    });

    // 2. Setup Main Navbar
    setupSlidingIndicator({
        containerId: 'main-nav',
        itemSelector: '.nav-item',
        indicatorId: 'nav-sliding-dot',
        activeClass: 'active-link'
    });
});
